import { getCmsServiceFromList } from "~/lib/utils";

/**
 * PACKAGE vouchers — choosing between equivalent packages.
 *
 * A PACKAGE voucher may list several package codes in `ServicesIncludedCodes`
 * that differ only in the treatment duration. Real case (voucher `VOU0004`,
 * verified against staging):
 *
 *   PAC_DAYSPA_M25_WEEKDAY -> ENTRANCE_DAY_SPA_WEEKDAY + MSG_25_SINGLE_TAILORTOUCH
 *   PAC_DAYSPA_M50_WEEKDAY -> ENTRANCE_DAY_SPA_WEEKDAY + MSG_50_SINGLE_TAILORTOUCH
 *   PAC_DAYSPA_M25_WEEKEND -> ENTRANCE_DAY_SPA_WEEKEND + MSG_25_SINGLE_TAILORTOUCH
 *   PAC_DAYSPA_M50_WEEKEND -> ENTRANCE_DAY_SPA_WEEKEND + MSG_50_SINGLE_TAILORTOUCH
 *
 * The weekday/weekend axis resolves itself: `Booking_Search` only returns the
 * packages available for the chosen date. The duration axis did not, and pushed
 * the flow into multi-package mode: `unifyPackageCompositions` merged steps > 1
 * by `StepCategory` (both `CAT_PACKAGE_STEP_TREATEMENTS`), so the time step
 * ended up offering two treatments to pick from. The default pick was array
 * order, and Axalta orders by `SellingOrder`, which puts the shorter variant
 * first: the sidebar showed the purchased package (50 min) while the time step
 * offered the 25.
 *
 * Business decision (17 Aug 2026): do not make the customer choose, always
 * offer the longest duration. Set back to `true` to re-enable the choice.
 */
// Typed as `boolean` rather than the `false` literal so TypeScript keeps
// type-checking both branches instead of treating one as dead code.
export const VOUCHER_PACKAGE_ALTERNATIVES_SELECTABLE: boolean = false;

function getPackageComposition(pkg: any): any[] {
  const composition = pkg?.apiService?.PackageComposition || pkg?.PackageComposition;
  return Array.isArray(composition) ? composition : [];
}

/**
 * Duration in minutes of a `HH:MM:SS` -> `HH:MM:SS` slot.
 *
 * "Daily" slots (`00:00:00` -> `23:59:59`) carry no real duration — that is how
 * the API marks services with no time band, and packages themselves use it — so
 * they count as 0.
 */
function slotDurationMinutes(slot: any): number {
  const start = typeof slot?.StartTime === "string" ? slot.StartTime : "";
  const end = typeof slot?.EndTime === "string" ? slot.EndTime : "";
  if (!start || !end) return 0;
  if (start === "00:00:00" && end === "23:59:59") return 0;

  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(":");
    const h = Number(hours);
    const m = Number(minutes);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
    return h * 60 + m;
  };

  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return 0;
  return Math.max(0, endMinutes - startMinutes);
}

/**
 * Treatment duration of a package, in minutes, read from the availability slots
 * of its steps > 1.
 *
 * `PackageComposition[].ServicesList[]` exposes no duration field
 * (`lifetime_minutes` only comes from `Booking_GetServices`, not from
 * `Booking_Search`), but every slot of a given service has the same width —
 * 30 min for `MSG_25_*`, 60 for `MSG_50_*` — so `EndTime - StartTime` is a
 * reliable discriminator and is already at hand.
 */
export function packageTreatmentDurationMinutes(pkg: any): number {
  let longest = 0;
  getPackageComposition(pkg).forEach((step: any) => {
    if (!step || (step.StepNr ?? 0) <= 1) return;
    const services = Array.isArray(step.ServicesList) ? step.ServicesList : [];
    services.forEach((service: any) => {
      const slots = Array.isArray(service?.Availability) ? service.Availability : [];
      slots.forEach((slot: any) => {
        const duration = slotDurationMinutes(slot);
        if (duration > longest) longest = duration;
      });
    });
  });
  return longest;
}

function entranceSignature(pkg: any): string {
  const step = getPackageComposition(pkg).find((s: any) => s?.StepNr === 1);
  const services = Array.isArray(step?.ServicesList) ? step.ServicesList : [];
  return services
    .map((s: any) => s?.Code)
    .filter(Boolean)
    .sort()
    .join("|");
}

function treatmentCodes(pkg: any): string[] {
  const codes: string[] = [];
  getPackageComposition(pkg).forEach((step: any) => {
    if (!step || (step.StepNr ?? 0) <= 1) return;
    const services = Array.isArray(step.ServicesList) ? step.ServicesList : [];
    services.forEach((service: any) => {
      if (service?.Code) codes.push(service.Code);
    });
  });
  return codes;
}

/**
 * `true` when the packages are duration variants of the same package: same
 * entrance (step 1) and treatments that belong to the same WordPress `servizio`
 * — i.e. several `code` entries in the ACF repeater of one post (the "Massaggio"
 * post lists both `MSG_25_SINGLE_TAILORTOUCH` and `MSG_50_SINGLE_TAILORTOUCH`).
 * This is the same grouping `ServiceDurationSelect` and
 * `groupVariantsByCmsService` already rely on.
 *
 * When the treatments are different WordPress services the packages are
 * genuinely different offers and the customer's choice is legitimate, so we do
 * NOT collapse. Without the WordPress data we answer `false`, which keeps the
 * previous behaviour.
 */
export function areDurationVariants(packages: any[], cmsServices: any[]): boolean {
  if (!Array.isArray(packages) || packages.length < 2) return false;

  const entrance = entranceSignature(packages[0]);
  if (!entrance) return false;
  if (!packages.every((pkg) => entranceSignature(pkg) === entrance)) return false;

  const cmsServiceIds = new Set<any>();
  for (const pkg of packages) {
    const codes = treatmentCodes(pkg);
    // Exactly one treatment per package: with richer compositions we would risk
    // hiding a real choice.
    if (codes.length !== 1) return false;
    const cmsService = getCmsServiceFromList(cmsServices || [], codes[0]);
    if (!cmsService?.id) return false;
    cmsServiceIds.add(cmsService.id);
  }

  return cmsServiceIds.size === 1;
}

/**
 * Among equivalent packages, returns the one with the longest treatment.
 * On a duration tie (or with no duration data) the first one wins, so the
 * behaviour matches the previous one.
 */
export function pickLongestTreatmentPackage(packages: any[]): any {
  if (!Array.isArray(packages) || packages.length === 0) return undefined;
  return packages.reduce((best, candidate) =>
    packageTreatmentDurationMinutes(candidate) >
    packageTreatmentDurationMinutes(best)
      ? candidate
      : best,
  );
}
